import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import DataTable, { Column } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import GuardCard from '../../components/GuardCard';
import GuardDetailModal from '../../components/GuardDetailModal';
import GuardFormModal from '../../components/GuardFormModal';
import { vendorApi } from '../../services/vendor';
import type { GuardListItem } from '../../types';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';

const columns: Column<GuardListItem>[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'external_id', header: 'External ID',
    render: (row) => <span className="text-slate-500">{row.external_id || '—'}</span>,
  },
  { key: 'assigned_sites_count', header: 'Sites', sortable: true },
  { key: 'score', header: 'Score', sortable: true,
    render: (row) => (
      <span className={`font-semibold ${getScoreColorClass(getScoreColor(row.score))}`}>
        {toGGScore(row.score)}
      </span>
    ),
  },
  { key: 'is_active', header: 'Status',
    render: (row) => (
      <StatusBadge
        label={row.is_active ? 'Active' : 'Inactive'}
        variant={row.is_active ? 'success' : 'neutral'}
      />
    ),
  },
];

export default function VendorGuards() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [detailGuardId, setDetailGuardId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['vendor', 'guards', page],
    queryFn: () => vendorApi.getGuards({ page: String(page) }).then((r) => r.data),
  });

  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <PageHeader
        title="Guards"
        description="Manage your security guards"
        actions={
          <div className="flex items-center gap-3">
            {/* Grid/Table toggle */}
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'table' ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                aria-label="Table view"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'grid' ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                aria-label="Grid view"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                </svg>
              </button>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700"
            >
              Add Guard
            </button>
          </div>
        }
      />

      {viewMode === 'table' ? (
        <DataTable<GuardListItem>
          columns={columns}
          data={data?.results ?? []}
          keyExtractor={(row) => row.id}
          isLoading={isLoading}
          emptyTitle="No guards yet"
          emptyDescription="Add your first guard to get started."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onRowClick={(row) => setDetailGuardId(row.id)}
        />
      ) : (
        <>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
            </div>
          ) : data?.results && data.results.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.results.map((guard) => (
                <GuardCard
                  key={guard.id}
                  guard={guard}
                  onClick={() => setDetailGuardId(guard.id)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-sm text-slate-500">No guards yet. Add your first guard to get started.</p>
            </div>
          )}

          {/* Pagination for grid view */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-3 py-1.5 text-sm text-slate-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {detailGuardId && (
        <GuardDetailModal
          guardId={detailGuardId}
          isOpen={!!detailGuardId}
          onClose={() => setDetailGuardId(null)}
        />
      )}

      {/* Create Modal */}
      <GuardFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(id) => navigate(`/vendor/guards/${id}`)}
      />
    </>
  );
}
